import {
  setupIonicReact,
  IonApp,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonTabs,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonLabel,
  IonIcon,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Redirect } from 'react-router-dom';
import { lazy, Suspense, Component, type ReactNode, useEffect } from 'react';
import { home, search, person, briefcase, planet } from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from '@tanstack/react-query';
import { LandingPage } from './components/landing/LandingPage';
import { RegisterPage } from './pages/Register';
import { SignInPage } from './pages/SignIn';
import { VerifyEmailPage } from './pages/VerifyEmail';
import { AboutPage, PrivacyPage, TermsPage, ContactPage, HelpPage, AIMatchingPage } from './pages/Info';
import { AgentsPage } from './pages/Agents';
import { AgentRegistrationWizard } from './components/wizards/AgentRegistrationWizard';
import { DonatePage } from './pages/Donate';
import { CandidateDashboard } from './pages/dashboard/CandidateDashboard';
import { ProfileManagement } from './pages/dashboard/ProfileManagement';
import { ExperienceManager } from './components/candidate/ExperienceManager';
import { PreferenceForm } from './components/candidate/PreferenceForm';
import { VerifyIdentityPage } from './pages/dashboard/VerifyIdentity';
import { EmployerDashboard } from './pages/dashboard/EmployerDashboard';
import { EmployerProfileManagement } from './pages/dashboard/EmployerProfileManagement';
import { LegalEntityManagement } from './pages/dashboard/LegalEntityManagement';
import { JobListings } from './pages/dashboard/JobListings';
import { JobEditPage } from './pages/dashboard/JobEditPage';
import { JobPostingWizard } from './components/wizards/JobPostingWizard';
import { CandidateSearch } from './pages/dashboard/CandidateSearch';
import { PrintableCV } from './pages/PrintableCV';
import { PWAUpdateToast } from './components/PWAUpdateToast';
import { UIToastHost } from './components/UIToastHost';
import { LocationProvider } from '@/lib/geo/LocationContext';
import './theme.css';
import { AppFooter } from '@/components/AppFooter';
import { restoreSession } from '@/lib/api/auth';

const SearchPage = lazy(() => import('./pages/Search').then((m) => ({ default: m.SearchPage })));
const JobsPage = lazy(() => import('./pages/Jobs').then((m) => ({ default: m.JobsPage })));
const JobDetail = lazy(() => import('./pages/JobDetail').then((m) => ({ default: m.JobDetail })));
const ExplorePage = lazy(() => import('./pages/Explore').then((m) => ({ default: m.ExplorePage })));
const ProviderProfile = lazy(() => import('./pages/ProviderProfile').then((m) => ({ default: m.ProviderProfile })));
const CandidateProfile = lazy(() => import('./pages/CandidateProfile').then((m) => ({ default: m.CandidateProfile })));
const SponsorServicePage = lazy(() => import('./pages/SponsorService').then((m) => ({ default: m.SponsorServicePage })));

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      console.error('[react-query] query failed:', error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error('[react-query] mutation failed:', error);
    },
  }),
});

setupIonicReact();

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <IonPage>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Something went wrong</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p className="bp-subheading">The page hit an unexpected error.</p>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', color: 'var(--ion-color-danger)' }}>
              {this.state.error.message}
            </pre>
            <button onClick={() => this.setState({ error: null })}>Try again</button>
          </IonContent>
        </IonPage>
      );
    }
    return this.props.children;
  }
}

function PageFallback() {
  return (
    <IonPage>
      <IonContent className="ion-padding">
        <p className="bp-subheading">Loading…</p>
            <AppFooter />

      </IonContent>
    </IonPage>
  );
}

const tabs = [
  { tab: 'home', icon: home, label: 'Home', path: '/home' },
  { tab: 'search', icon: search, label: 'Search', path: '/search' },
  { tab: 'explore', icon: planet, label: 'Explore', path: '/explore' },
  { tab: 'jobs', icon: briefcase, label: 'Jobs', path: '/jobs' },
  { tab: 'profile', icon: person, label: 'Profile', path: '/profile' },
];

export function App() {
  useEffect(() => {
    restoreSession();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LocationProvider>
        <IonApp>
        <IonReactRouter>
          <AppRoutes />
          <PWAUpdateToast />
          <UIToastHost />
        </IonReactRouter>
      </IonApp>
      </LocationProvider>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();
  const isPrintRoute = /^\/candidate\/[^/]+\/print$/.test(pathname);

  return (
    <IonTabs>
      <IonRouterOutlet>
        <Route exact path="/candidate/:slug/print" component={PrintableCV} />
              <Route exact path="/home" component={LandingPage} />
              <Route exact path="/register" component={RegisterPage} />
              <Route exact path="/signin" component={SignInPage} />
              <Route exact path="/verify-email" component={VerifyEmailPage} />
              <Route exact path="/about" component={AboutPage} />
              <Route exact path="/privacy" component={PrivacyPage} />
              <Route exact path="/terms" component={TermsPage} />
              <Route exact path="/contact" component={ContactPage} />
              <Route exact path="/help" component={HelpPage} />
              <Route exact path="/ai-matching" component={AIMatchingPage} />
              <Route exact path="/donate" component={DonatePage} />
              <Route exact path="/agents" component={AgentsPage} />
              <Route exact path="/agents/apply" component={AgentRegistrationWizard} />
              <Route exact path="/search" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <SearchPage />
                  </Suspense>
                </ErrorBoundary>
              )} />
              <Route exact path="/explore" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <ExplorePage />
                  </Suspense>
                </ErrorBoundary>
              )} />
              <Route exact path="/jobs" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <JobsPage />
                  </Suspense>
                </ErrorBoundary>
              )} />
              <Route exact path="/jobs/:id" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <JobDetail />
                  </Suspense>
                </ErrorBoundary>
              )} />
              <Route exact path="/profile/edit" component={ProfileManagement} />
              <Route exact path="/profile/experience" component={ExperienceManager} />
              <Route exact path="/profile/preferences" component={PreferenceForm} />
              <Route exact path="/profile/verify-identity" component={VerifyIdentityPage} />
              <Route exact path="/profile/legal-entity/:id?" component={LegalEntityManagement} />
              <Route path="/profile" render={() => <CandidateDashboard profile={null} />} />
              <Route exact path="/employer" component={EmployerDashboard} />
              <Route exact path="/employer/profile" component={EmployerProfileManagement} />
              <Route exact path="/employer/jobs" component={JobListings} />
              <Route exact path="/employer/jobs/new" component={JobPostingWizard} />
              <Route exact path="/employer/jobs/:id/edit" component={JobEditPage} />
              <Route exact path="/employer/search" component={CandidateSearch} />
              <Route exact path="/candidate/:slug" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <CandidateProfile />
                  </Suspense>
                </ErrorBoundary>
              )} />
              {/* Public sponsored-services entry — registered like other public
                  routes so tab outlets cannot swallow the deep link (M07 S06). */}
              <Route exact path="/sponsor/:slug" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <SponsorServicePage />
                  </Suspense>
                </ErrorBoundary>
              )} />
              <Route exact path="/providers/:id" render={() => (
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <ProviderProfile />
                  </Suspense>
                </ErrorBoundary>
              )} />
              <Redirect exact from="/" to="/home" />
            </IonRouterOutlet>
            {!isPrintRoute && (
            <IonTabBar slot="bottom">
              {tabs.map((t) => (
                <IonTabButton key={t.tab} tab={t.tab} href={t.path}>
                  <IonIcon icon={t.icon} />
                  <IonLabel>{t.label}</IonLabel>
                </IonTabButton>
              ))}
            </IonTabBar>
            )}
          </IonTabs>
  );
}
